import React, { useState, useEffect } from "react";
import { View, Text, InputField, Badge, Card } from "../components/base";
import { Icon } from "../components/base/Icon";
import { Tooltip } from "../components/base/Tooltip";
import { PageLayout } from "../components/PageLayout";
import { ProviderLogo } from "../components/ProviderLogo";
import { makeRequest } from "../utils/request";

interface VoiceProfile {
  publicId: string;
  name: string;
  provider: string;
  gender: string;
  primaryLanguage: string;
  accent?: string;
  region?: string;
  voiceStyle?: string;
  description?: string;
  tags?: string[];
  useCases?: string[];
  similarity?: number;
}

interface VoiceSearchFilters {
  provider?: string;
  gender?: string;
  primaryLanguage?: string;
  accent?: string;
}

export const VoiceLibraryPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [allVoices, setAllVoices] = useState<VoiceProfile[]>([]); // All voices loaded once
  const [searchResults, setSearchResults] = useState<VoiceProfile[]>([]); // Results from semantic search
  const [displayedVoices, setDisplayedVoices] = useState<VoiceProfile[]>([]); // What user sees
  const [isSearchActive, setIsSearchActive] = useState(false); // Track if we're in search mode
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // Loading all voices on mount
  const [filters, setFilters] = useState<VoiceSearchFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; voice?: VoiceProfile }>({ isOpen: false });
  const [previewText, setPreviewText] = useState(
    "Hello, this is a preview of my voice. I can help you with various tasks and speak in a natural, friendly tone.",
  );
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);

  // Helper functions for badge colors - slate default, pink when matching active filters
  const getProviderBadgeClass = (provider: string) => {
    const isActiveFilter = filters.provider && filters.provider.toLowerCase() === provider.toLowerCase();
    return isActiveFilter ? "bg-pink-500 text-white hover:bg-pink-600 border-transparent" : "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
  };

  const getGenderBadgeClass = (gender: string) => {
    const isActiveFilter = filters.gender && filters.gender.toLowerCase() === gender.toLowerCase();
    return isActiveFilter ? "bg-pink-500 text-white hover:bg-pink-600 border-transparent" : "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
  };

  const getLanguageBadgeClass = (language: string) => {
    // Handle both language code and full name matching
    const activeLanguage = filters.primaryLanguage;
    if (!activeLanguage) {
      return "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
    }

    const languageMapping: Record<string, string> = {
      english: "en",
      chinese: "zh",
      spanish: "es",
    };

    const filterValue = languageMapping[activeLanguage.toLowerCase()] || activeLanguage.toLowerCase();
    const voiceLanguage = languageMapping[language.toLowerCase()] || language.toLowerCase();
    const isActiveFilter = filterValue === voiceLanguage;

    return isActiveFilter ? "bg-pink-500 text-white hover:bg-pink-600 border-transparent" : "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
  };

  // Available filter options
  const providerOptions = ["OpenAI", "azure", "google", "cartesia", "prim", "minimax"];
  const genderOptions = ["male", "female"];
  const languageOptions = ["English", "Chinese", "Spanish"];

  // Client-side filtering function
  const applyClientSideFilters = (voices: VoiceProfile[]): VoiceProfile[] => {
    return voices.filter((voice) => {
      // Provider filter
      if (filters.provider && voice.provider.toLowerCase() !== filters.provider.toLowerCase()) {
        return false;
      }

      // Gender filter
      if (filters.gender && voice.gender.toLowerCase() !== filters.gender.toLowerCase()) {
        return false;
      }

      // Language filter with mapping
      if (filters.primaryLanguage) {
        const languageMapping: Record<string, string> = {
          english: "en",
          chinese: "zh",
          spanish: "es",
        };

        const filterValue = languageMapping[filters.primaryLanguage.toLowerCase()] || filters.primaryLanguage.toLowerCase();
        const voiceLanguage = languageMapping[voice.primaryLanguage.toLowerCase()] || voice.primaryLanguage.toLowerCase();

        if (filterValue !== voiceLanguage) {
          return false;
        }
      }

      // Accent filter
      if (filters.accent && !voice.accent?.toLowerCase().includes(filters.accent.toLowerCase())) {
        return false;
      }

      return true;
    });
  };

  // Load all voices once on mount
  const loadAllVoices = async () => {
    setInitialLoading(true);
    try {
      const response = await makeRequest<{ data: VoiceProfile[] }>("/v1/voice-search");
      setAllVoices(response.data);
      setDisplayedVoices(response.data); // Show all initially
    } catch (error) {
      console.error("Error loading voices:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Apply filters when filters change (client-side)
  React.useEffect(() => {
    if (isSearchActive && searchResults.length > 0) {
      // Filter search results when in search mode
      const filtered = applyClientSideFilters(searchResults);
      setDisplayedVoices(filtered);
    } else if (!isSearchActive && allVoices.length > 0) {
      // Filter all voices when not searching
      const filtered = applyClientSideFilters(allVoices);
      setDisplayedVoices(filtered);
    }
  }, [filters, allVoices, searchResults, isSearchActive]);

  // Load all voices on mount
  React.useEffect(() => {
    loadAllVoices();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // No search query - exit search mode and show filtered all voices
      setIsSearchActive(false);
      setSearchResults([]);
      const filtered = applyClientSideFilters(allVoices);
      setDisplayedVoices(filtered);
      return;
    }

    setLoading(true);
    setIsSearchActive(true);
    try {
      const params = new URLSearchParams();
      params.append("query", searchQuery);

      // Don't send filters to server for semantic search - we'll apply them client-side
      // This gives us more flexibility to filter the semantic results
      const response = await makeRequest<{ data: VoiceProfile[] }>(`/v1/voice-search?${params.toString()}`);

      // Store the raw search results
      setSearchResults(response.data);

      // Apply current filters to the search results
      const filtered = applyClientSideFilters(response.data);
      setDisplayedVoices(filtered);
    } catch (error) {
      console.error("Error searching voices:", error);
      setIsSearchActive(false);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllVoices = () => {
    // Clear search query and exit search mode
    setSearchQuery("");
    setIsSearchActive(false);
    setSearchResults([]);
    const filtered = applyClientSideFilters(allVoices);
    setDisplayedVoices(filtered);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setFilters({});
  };

  const removeFilter = (filterKey: keyof VoiceSearchFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handlePreview = (voice: VoiceProfile) => {
    setPreviewModal({ isOpen: true, voice });
    setAudioUrl(null); // Reset previous audio
  };

  const handleGeneratePreview = async () => {
    if (!previewModal.voice || !previewText.trim()) return;

    setGeneratingAudio(true);
    try {
      const response = await makeRequest<Blob>("/v1/tts", {
        method: "POST",
        body: {
          text: previewText,
          voice: previewModal.voice.name,
        },
        blob: true,
      });

      if (response.size === 0) {
        throw new Error("Empty audio response");
      }

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl); // Clean up previous audio
      }

      const url = URL.createObjectURL(response);
      setAudioUrl(url);
    } catch (error) {
      console.error("Failed to generate preview:", error);
      alert("Failed to generate audio preview. Please try again.");
    } finally {
      setGeneratingAudio(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false });
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  return (
    <PageLayout
      title="Voice Library"
      description="Search for voices using natural language. Try queries like 'warm female voice for customer service' or 'professional male narrator'."
    >
      <View className="flex flex-col gap-4 mt-6">
        {/* Search Bar and Controls */}
        <View className="flex flex-col sm:flex-row gap-2">
          <View className="flex-1">
            <InputField
              placeholder="Search for voices... (e.g., 'warm female voice for customer service')"
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onKeyDown={handleKeyDown}
              className="w-full"
            />
          </View>
          <View className="flex flex-row items-center gap-2">
            <button
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
              className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon
                name="search"
                size={14}
              />
              {loading ? "Searching..." : "Search"}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                showFilters
                  ? "bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <Icon
                name="filter"
                size={14}
              />
              Filters
              {activeFilterCount > 0 && (
                <Badge
                  variant="default"
                  className="ml-0.5 text-[10px] bg-pink-500 text-white hover:bg-pink-600 border-transparent"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </button>
            <button
              onClick={handleShowAllVoices}
              disabled={loading || initialLoading}
              className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Show All
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="flex flex-row items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
              >
                <Icon
                  name="close"
                  size={12}
                />
                Clear
              </button>
            )}
          </View>
        </View>

        {/* Active Filters */}
        {activeFilterCount > 0 && (
          <View className="flex flex-row flex-wrap gap-2">
            {Object.entries(filters).map(([key, value]) =>
              value ? (
                <Badge
                  key={key}
                  variant="default"
                  className="flex items-center gap-1 bg-pink-500 text-white hover:bg-pink-600 border-transparent"
                >
                  {key}: {value}
                  <button
                    onClick={() => removeFilter(key as keyof VoiceSearchFilters)}
                    className="ml-1 hover:text-red-200"
                  >
                    ×
                  </button>
                </Badge>
              ) : null,
            )}
          </View>
        )}

        {/* Filter Panel */}
        {showFilters && (
          <View className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
            <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <View className="flex flex-col gap-1.5">
                <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Provider</Text>
                <select
                  value={filters.provider || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, provider: e.target.value || undefined }))}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">All Providers</option>
                  {providerOptions.map((provider) => (
                    <option
                      key={provider}
                      value={provider}
                    >
                      {provider.charAt(0).toUpperCase() + provider.slice(1)}
                    </option>
                  ))}
                </select>
              </View>

              <View className="flex flex-col gap-1.5">
                <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Gender</Text>
                <select
                  value={filters.gender || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value || undefined }))}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">All Genders</option>
                  {genderOptions.map((gender) => (
                    <option
                      key={gender}
                      value={gender}
                    >
                      {gender.charAt(0).toUpperCase() + gender.slice(1)}
                    </option>
                  ))}
                </select>
              </View>

              <View className="flex flex-col gap-1.5">
                <View className="flex flex-row items-center gap-1">
                  <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Primary Language</Text>
                  <Tooltip content="The native language this voice was trained on and sounds most natural speaking.">
                    <Icon
                      name="info"
                      size={12}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help"
                    />
                  </Tooltip>
                </View>
                <select
                  value={filters.primaryLanguage || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, primaryLanguage: e.target.value || undefined }))}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">All Languages</option>
                  {languageOptions.map((language) => (
                    <option
                      key={language}
                      value={language}
                    >
                      {language}
                    </option>
                  ))}
                </select>
              </View>

              <View className="flex flex-col gap-1.5">
                <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Accent</Text>
                <InputField
                  placeholder="e.g., American, British"
                  value={filters.accent || ""}
                  onChange={(value) => setFilters((prev) => ({ ...prev, accent: value || undefined }))}
                />
              </View>
            </View>
          </View>
        )}
      </View>

      {/* Results */}
      <View className="flex flex-col gap-4 mt-4">
        {(loading || initialLoading) && (
          <View className="flex items-center justify-center py-12">
            <Text className="text-sm text-gray-500 dark:text-gray-400">{initialLoading ? "Loading voices..." : "Searching..."}</Text>
          </View>
        )}

        {!loading && !initialLoading && displayedVoices.length === 0 && (
          <View className="flex items-center justify-center py-12">
            <Text className="text-sm text-gray-500 dark:text-gray-400">No voices found. Try adjusting your search query or filters.</Text>
          </View>
        )}

        {!loading && !initialLoading && displayedVoices.length > 0 && (
          <>
            <View className="flex flex-row items-center justify-between">
              <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {displayedVoices.length} voice{displayedVoices.length !== 1 ? "s" : ""}
                {searchQuery && ` for "${searchQuery}"`}
              </Text>
            </View>

            <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {displayedVoices.map((voice) => (
                <Card
                  key={voice.publicId}
                  className="p-4 transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 group"
                >
                  {/* Header: name + provider */}
                  <View className="flex flex-row items-center justify-between gap-3 mb-3">
                    <Text className="text-sm font-medium text-gray-900 dark:text-white flex-1 min-w-0 truncate">{voice.name}</Text>
                    <View className="flex flex-row items-center gap-2 shrink-0">
                      <div
                        className={
                          voice.provider.toLowerCase() === "prim"
                            ? "[--logo-fill:theme(colors.brand)] dark:[--logo-fill:theme(colors.cyan.400)] [--logo-stroke:theme(colors.slate.900)] dark:[--logo-stroke:theme(colors.slate.600)] [--logo-stroke-width:5]"
                            : ""
                        }
                      >
                        <ProviderLogo
                          provider={voice.provider}
                          size={["google", "prim", "minimax"].includes(voice.provider.toLowerCase()) ? "md" : "sm"}
                        />
                      </div>
                      {voice.similarity && (
                        <Badge
                          variant="info"
                          className="text-[10px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                        >
                          {Math.round(voice.similarity * 100)}%
                        </Badge>
                      )}
                    </View>
                  </View>

                  {/* Badges */}
                  <View className="flex flex-row flex-wrap gap-1.5 mb-3">
                    {voice.gender && (
                      <Badge
                        variant="default"
                        className={`text-[10px] ${getGenderBadgeClass(voice.gender)}`}
                      >
                        {voice.gender}
                      </Badge>
                    )}
                    {voice.primaryLanguage && (
                      <Badge
                        variant="default"
                        className={`text-[10px] ${getLanguageBadgeClass(voice.primaryLanguage)}`}
                      >
                        {voice.primaryLanguage}
                      </Badge>
                    )}
                  </View>

                  {/* Detail rows */}
                  <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                    {voice.description && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="info"
                          size={12}
                          className="text-gray-400 shrink-0"
                        />
                        <span
                          className="text-[11px] text-gray-500 dark:text-gray-400 truncate"
                          title={voice.description}
                        >
                          {voice.description}
                        </span>
                      </View>
                    )}
                    {voice.voiceStyle && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="voice-over"
                          size={12}
                          className="text-purple-400 shrink-0"
                        />
                        <Text className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">{voice.voiceStyle}</Text>
                      </View>
                    )}
                    {voice.accent && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="language"
                          size={12}
                          className="text-blue-400 shrink-0"
                        />
                        <Text className="text-[11px] text-gray-500 dark:text-gray-400 capitalize flex-1">{voice.accent}</Text>
                      </View>
                    )}
                  </View>

                  {/* Preview button */}
                  <View className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => handlePreview(voice)}
                      className="flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 w-full justify-center"
                    >
                      <Icon
                        name="play"
                        size={12}
                      />
                      Preview
                    </button>
                  </View>
                </Card>
              ))}
            </View>
          </>
        )}
      </View>

      {/* Preview Modal */}
      {previewModal.isOpen && previewModal.voice && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex flex-row items-center justify-between mb-4">
              <Text className="text-sm font-semibold text-gray-900 dark:text-white">{previewModal.voice.name}</Text>
              <button
                onClick={closePreviewModal}
                className="flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <Icon
                  name="close"
                  size={16}
                />
              </button>
            </div>

            <div className="flex flex-row flex-wrap items-center gap-2 mb-4">
              <div
                className={
                  previewModal.voice.provider.toLowerCase() === "prim"
                    ? "flex items-center h-6 [--logo-fill:theme(colors.brand)] dark:[--logo-fill:theme(colors.cyan.400)] [--logo-stroke:theme(colors.slate.900)] dark:[--logo-stroke:theme(colors.slate.600)] [--logo-stroke-width:5]"
                    : "flex items-center h-6"
                }
              >
                <ProviderLogo
                  provider={previewModal.voice.provider}
                  size={["google", "prim", "minimax"].includes(previewModal.voice.provider.toLowerCase()) ? "md" : "sm"}
                />
              </div>
              <Badge
                variant="default"
                className={`text-[10px] ${getGenderBadgeClass(previewModal.voice.gender)}`}
              >
                {previewModal.voice.gender}
              </Badge>
              <Badge
                variant="default"
                className={`text-[10px] ${getLanguageBadgeClass(previewModal.voice.primaryLanguage)}`}
              >
                {previewModal.voice.primaryLanguage}
              </Badge>
            </div>

            <div className="flex flex-col gap-1.5 mb-4">
              <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Preview Text</Text>
              <textarea
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:focus:border-blue-500 transition-colors"
                rows={3}
                placeholder="Enter text to preview..."
              />
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleGeneratePreview}
                disabled={generatingAudio || !previewText.trim()}
                className="flex flex-row items-center justify-center gap-1.5 w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors border bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Icon
                  name="play"
                  size={14}
                />
                {generatingAudio ? "Generating..." : "Generate Preview"}
              </button>

              {audioUrl && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <audio
                    controls
                    src={audioUrl}
                    className="w-full"
                    autoPlay
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
